import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { taskApi, TaskListParams } from '../api/taskApi'
import { useToast } from '@/hooks/use-toast'
import type { TaskStatus, TaskType, ArtifactType } from '@/types/entities'

const TASKS_KEY = 'tasks'
const ACTIVE_TASKS_KEY = 'active-tasks'
const TASK_ARTIFACTS_KEY = 'task-artifacts'

export function useTasks(params: TaskListParams = {}) {
  return useQuery({
    queryKey: [TASKS_KEY, params],
    queryFn: () => taskApi.getTasks(params),
    refetchInterval: 5000, // Poll every 5 seconds for updates
  })
}

export function useActiveTasks(limit = 50, offset = 0, enabled = true) {
  return useQuery({
    queryKey: [ACTIVE_TASKS_KEY, limit, offset],
    queryFn: () => taskApi.getActiveTasks(limit, offset),
    refetchInterval: enabled ? 3000 : false, // Poll more frequently for active tasks
    enabled,
  })
}

export function useTask(taskId: string | null) {
  return useQuery({
    queryKey: [TASKS_KEY, taskId],
    queryFn: () => taskApi.getTask(taskId!),
    enabled: !!taskId,
    refetchInterval: 2000, // Poll frequently for task details
  })
}

export function useTaskDetails(taskId: string | null) {
  return useQuery({
    queryKey: [TASKS_KEY, taskId, 'details'],
    queryFn: () => taskApi.getTaskDetails(taskId!),
    enabled: !!taskId,
    refetchInterval: 2000, // Poll frequently for task details
  })
}

export function useTaskArtifacts(taskId: string | null) {
  return useQuery({
    queryKey: [TASK_ARTIFACTS_KEY, taskId],
    queryFn: () => taskApi.getTaskArtifacts(taskId!),
    enabled: !!taskId,
    refetchInterval: 5000, // Poll less frequently for artifacts
  })
}

export function useCancelTask() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ taskId, force = false }: { taskId: string; force?: boolean }) => 
      taskApi.cancelTask(taskId, force),
    onSuccess: (task, { force }) => {
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] })
      queryClient.invalidateQueries({ queryKey: [ACTIVE_TASKS_KEY] })
      toast({
        title: force ? 'Task force-cancelled' : 'Task cancelled',
        description: `Task ${task.id.slice(0, 8)}... has been cancelled.`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to cancel task',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useRetryTask() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (taskId: string) => taskApi.retryTask(taskId),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] })
      queryClient.invalidateQueries({ queryKey: [ACTIVE_TASKS_KEY] })
      toast({
        title: 'Task retried',
        description: `Task ${task.id.slice(0, 8)}... has been queued for retry.`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to retry task',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// Helper to get human-readable task type
export function getTaskTypeLabel(type: TaskType): string {
  switch (type) {
    case 'PUBLISH_ARTWORK':
      return 'Publish Artwork'
    case 'UNPUBLISH_ARTWORK':
      return 'Unpublish Artwork'
    case 'ANALYZE_ARTWORK':
      return 'Analyze Artwork'
    default:
      return type
  }
}

// Helper to get human-readable task status
export function getTaskStatusLabel(status: TaskStatus): string {
  switch (status) {
    case 'PENDING':
      return 'Pending'
    case 'RUNNING':
      return 'Running'
    case 'COMPLETED':
      return 'Completed'
    case 'FAILED':
      return 'Failed'
    case 'CANCELLED':
      return 'Cancelled'
    default:
      return status
  }
}

// Helper to get status color variant
export function getTaskStatusVariant(status: TaskStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PENDING':
      return 'secondary'
    case 'RUNNING':
      return 'default'
    case 'COMPLETED':
      return 'outline'
    case 'FAILED':
      return 'destructive'
    case 'CANCELLED':
      return 'secondary'
    default:
      return 'default'
  }
}

// Helper to get artifact type label
export function getArtifactTypeLabel(type: ArtifactType): string {
  switch (type) {
    case 'PRODUCT_IMAGE':
      return 'Product Image'
    case 'PREVIEW_IMAGE':
      return 'Preview Image'
    case 'EXPORT_FILE':
      return 'Export File'
    default:
      return type
  }
}

// Helper to format file size
export function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
